import React, { FC, useCallback, useMemo } from 'react';
import Dropdown from 'components/Elements/Dropdown';
import { DropdownOption } from 'components/Elements/Dropdown/types';
import { SiteAreaAccessType, SiteAreaAccessTypeQuestion } from 'core/accessRequestForm/types';
import DynamicQuestions from '../DynamicQuestions';
import FormSection from '../FormSection';
import './accessTypeSelector.scss';

interface AccessTypeSelectorProps {
    accessTypes: SiteAreaAccessType[];
    selectedAccessTypeId: number | null;
    onChange: (accessTypeId: number | null) => void;
    questions: SiteAreaAccessTypeQuestion[];
    answers: Record<number, string>;
    onAnswerChange: (questionId: number, value: string) => void;
}

const AccessTypeSelector: FC<AccessTypeSelectorProps> = ({
    accessTypes,
    selectedAccessTypeId,
    onChange,
    questions,
    answers,
    onAnswerChange,
}) => {
    const options: DropdownOption[] = useMemo(() => accessTypes.map((at) => ({
        label: at.name,
        value: String(at.id),
    })), [accessTypes]);

    const selectedValues: DropdownOption[] = useMemo(() => {
        if (selectedAccessTypeId === null) return [];
        const found = options.find((o) => o.value === String(selectedAccessTypeId));
        return found ? [found] : [];
    }, [selectedAccessTypeId, options]);

    const handleSelect = useCallback((values: DropdownOption[]) => {
        if (values.length === 0) {
            onChange(null);
        } else {
            const value = values[0]?.value;
            onChange(value ? Number(value) : null);
        }
    }, [onChange]);

    return (
        <FormSection title="Access Type">
            <div className="access-type-selector-container">
                {accessTypes.length === 0 ? (
                    <p className="empty-state">No access types have been configured for this site.</p>
                ) : (
                    <>
                        <Dropdown
                            options={options}
                            onSelect={handleSelect}
                            values={selectedValues}
                            placeholder="Select access type" />
                        {questions.length > 0 && (
                            <DynamicQuestions
                                questions={questions}
                                answers={answers}
                                onChange={onAnswerChange} />
                        )}
                    </>
                )}
            </div>
        </FormSection>
    );
};

export default AccessTypeSelector;
